import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Tag } from 'primereact/tag';
import {
  AlertTriangle,
  Calculator,
  CheckCircle,
  Clock,
  Target,
} from 'lucide-react';
import {
  CourseFinancialData,
  YearlyProgramExpenseReport,
} from '../../../../../api/types/reports/finanace/program-breakdown/incomes/program-financial-breakdown-expanses.type';
import { numberToHebrewLetter } from '../../../../../utils/hebrew-date-utils';
import Colors from '../../../../../utils/colors';
import { Tooltip } from 'primereact/tooltip';
import { LecturerRateDisplayType } from '../../../../../api/types/lecturers/price-rate/lecturer-rate-display-type';

export interface ProgramFinancialExpensesDatamodel {
  data: YearlyProgramExpenseReport;
}

interface CourseExpenseSummary {
  courseId: number;
  courseName: string;
  totalHours: number; // מכסת שעות
  scheduledHours: number; // שעות ששובצו
  actualHours: number; // שעות שבוצעו
  plannedAmount: number; // סכום מתוכנן
  actualAmount: number; // סכום שבוצע
}

interface YearOption {
  id: number;
  label: string;
}

interface AsdData {
  title: string;
  str: string;
  icon?: React.ReactNode;
}

const Asd: React.FC<AsdData> = ({ str, title, icon }) => {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: 0,
        minHeight: '120px',
        flex: 1,
        gap: '24px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <span
          style={{
            fontSize: '1.1rem',
            color: '#64748b',
            fontWeight: 500,
            marginBottom: 8,
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: '#0f172a',
            letterSpacing: '-1px',
          }}
        >
          {str}
        </span>
      </div>
      <div
        style={{
          background: 'linear-gradient(135deg, #f3f4f6 0%, #e0e7ef 100%)',
          borderRadius: '50%',
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        {icon}
      </div>
    </div>
  );
};

// Custom Year Filter Component
const YearFilterChips: React.FC<{
  yearOptions: YearOption[];
  selectedYears: number[];
  onToggleYear: (yearId: number) => void;
}> = ({ yearOptions, selectedYears, onToggleYear }) => {
  const chipStyle = (isSelected: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 16px',
    margin: '4px',
    borderRadius: '20px',
    border: isSelected ? 'none' : '2px solid #e2e8f0',
    background: isSelected
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : '#fff',
    color: isSelected ? '#fff' : '#64748b',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: isSelected
      ? '0 4px 15px rgba(102, 126, 234, 0.3)'
      : '0 2px 8px rgba(0,0,0,0.06)',
    transform: isSelected ? 'translateY(-1px)' : 'translateY(0)',
    userSelect: 'none' as const,
  });

  const containerStyle: CSSProperties = {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    direction: 'rtl',
    textAlign: 'right',
  };

  const headerStyle: CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
    textAlign: 'right',
    direction: 'rtl',
  };

  const chipsContainerStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start', // Start from right in RTL context
    direction: 'rtl',
    textAlign: 'right',
  };

  const allSelectedButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '16px',
    border: '1px solid #cbd5e1',
    background: '#f8fafc',
    color: '#475569',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    userSelect: 'none' as const,
    direction: 'rtl',
  };

  const handleSelectAll = () => {
    if (selectedYears.length === yearOptions.length) {
      // Deselect all
      yearOptions.forEach((year) => {
        if (selectedYears.includes(year.id)) {
          onToggleYear(year.id);
        }
      });
    } else {
      // Select all
      yearOptions.forEach((year) => {
        if (!selectedYears.includes(year.id)) {
          onToggleYear(year.id);
        }
      });
    }
  };

  const isAllSelected = selectedYears.length === yearOptions.length;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>סינון לפי שנים</div>
      <div style={chipsContainerStyle}>
        <div
          style={allSelectedButtonStyle}
          onClick={handleSelectAll}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f8fafc';
          }}
        >
          {isAllSelected ? 'בטל הכל' : 'בחר הכל'}
        </div>
        {yearOptions.map((year) => {
          const isSelected = selectedYears.includes(year.id);
          return (
            <div
              key={year.id}
              style={chipStyle(isSelected)}
              onClick={() => onToggleYear(year.id)}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 12px rgba(0,0,0,0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 8px rgba(0,0,0,0.06)';
                }
              }}
            >
              {year.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ProgramFinancialExpenses: React.FC<
  ProgramFinancialExpensesDatamodel
> = ({ data }) => {
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [chartData1, setChartData1] = useState({});
  const [chartData2, setChartData2] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  const style1 = {
    textAlign: 'right',
    maxWidth: '120px',
    minWidth: '120px',
  } as CSSProperties;

  // Create year options from the available data
  const yearOptions: YearOption[] = React.useMemo(() => {
    if (!data) return [];

    const availableYears = Object.keys(data).map(Number).sort();
    return availableYears.map((year) => ({
      id: year,
      label:
        year === -1
          ? 'קורסים ביתרה לשיבוץ'
          : `שנה ${numberToHebrewLetter(year)}׳`,
    }));
  }, [data]);

  // Initialize selected years to include all available years
  React.useEffect(() => {
    if (yearOptions.length > 0 && selectedYears.length === 0) {
      setSelectedYears(yearOptions.map((year) => year.id));
    }
  }, [yearOptions]);

  const toggleYear = (yearId: number) => {
    setSelectedYears((prev) =>
      prev.includes(yearId)
        ? prev.filter((id) => id !== yearId)
        : [...prev, yearId],
    );
  };

  // Filter data based on selected years
  const filteredData = React.useMemo(() => {
    if (!data || selectedYears.length === 0) return {};

    const filtered: YearlyProgramExpenseReport = {};
    selectedYears.forEach((year) => {
      if (data[year]) {
        filtered[year] = data[year];
      }
    });
    return filtered;
  }, [data, selectedYears]);

  // Memoize the calculation to prevent constant re-renders - now using filtered data
  const { courseSummaries, totalStats } = useMemo(() => {
    let totalHoursQuota = 0;
    let totalScheduledHours = 0;
    let totalActualHours = 0;
    let totalPlannedAmount = 0;
    let totalActualAmount = 0;

    const summaries: CourseExpenseSummary[] = [];

    if (filteredData) {
      Object.values(filteredData)
        .flat()
        .forEach((course: CourseFinancialData) => {
          totalHoursQuota += course.courseHours;

          // Calculate scheduled hours (sum of all lecture durations)
          const scheduledHours = course.lectures.reduce((sum, lecture) => {
            const duration =
              lecture.endTime && lecture.startTime
                ? (new Date(lecture.endTime).getTime() -
                    new Date(lecture.startTime).getTime()) /
                  (1000 * 60 * 60)
                : 0;
            return sum + duration;
          }, 0);

          // Calculate actual hours (only lectures that have been conducted)
          const actualHours = course.lectures.reduce((sum, lecture) => {
            if (lecture.actualStartAt && lecture.actualEndAt) {
              const duration =
                (new Date(lecture.actualEndAt).getTime() -
                  new Date(lecture.actualStartAt).getTime()) /
                (1000 * 60 * 60);
              return sum + duration;
            }
            return sum;
          }, 0);

          // Calculate planned amount (scheduled hours * rate)
          const plannedAmount = course.lectures.reduce((sum, lecture) => {
            const duration =
              lecture.endTime && lecture.startTime
                ? (new Date(lecture.endTime).getTime() -
                    new Date(lecture.startTime).getTime()) /
                  (1000 * 60 * 60)
                : 0;

            const ratePerHour =
              lecture.displayType === LecturerRateDisplayType.AcademicHour
                ? lecture.rate * (60 / 45) // Convert academic hour rate to hourly rate
                : lecture.rate;
            return sum + duration * ratePerHour;
          }, 0);

          // Calculate actual amount (actual hours * rate)
          const actualAmount = course.lectures.reduce((sum, lecture) => {
            if (lecture.actualStartAt && lecture.actualEndAt) {
              const duration =
                (new Date(lecture.actualEndAt).getTime() -
                  new Date(lecture.actualStartAt).getTime()) /
                (1000 * 60 * 60);

              const ratePerHour =
                lecture.displayType === LecturerRateDisplayType.AcademicHour
                  ? lecture.rate * (60 / 45) // Convert academic hour rate to hourly rate
                  : lecture.rate;

              return sum + duration * ratePerHour;
            }
            return sum;
          }, 0);

          course.lectures.forEach((lecture) => {
            // Calculate scheduled hours for totals
            if (lecture.endTime && lecture.startTime) {
              const duration =
                (new Date(lecture.endTime).getTime() -
                  new Date(lecture.startTime).getTime()) /
                (1000 * 60 * 60);

              const ratePerHour =
                lecture.displayType === LecturerRateDisplayType.AcademicHour
                  ? lecture.rate * (60 / 45) // Convert academic hour rate to hourly rate
                  : lecture.rate;

              totalScheduledHours += duration;
              totalPlannedAmount += duration * ratePerHour;
            }

            // Calculate actual hours for totals
            if (lecture.actualStartAt && lecture.actualEndAt) {
              const duration =
                (new Date(lecture.actualEndAt).getTime() -
                  new Date(lecture.actualStartAt).getTime()) /
                (1000 * 60 * 60);
              totalActualHours += duration;

              const ratePerHour =
                lecture.displayType === LecturerRateDisplayType.AcademicHour
                  ? lecture.rate * (60 / 45) // Convert academic hour rate to hourly rate
                  : lecture.rate;

              totalActualAmount += duration * ratePerHour;
            }
          });

          summaries.push({
            courseId: course.courseId,
            courseName: course.courseName,
            totalHours: course.courseHours,

            scheduledHours: course.scheduledHours,
            actualHours: course.hoursActuallyWorked,
            plannedAmount: course.expectedExpenses,
            actualAmount: course.actualExpenses,

            /*            scheduledHours: Math.round(scheduledHours * 100) / 100,
            actualHours: Math.round(actualHours * 100) / 100,
            plannedAmount: Math.round(plannedAmount),
            actualAmount: Math.round(actualAmount),*/
          });
        });
    }

    const changeableBalance = totalPlannedAmount - totalActualAmount;

    return {
      courseSummaries: summaries,
      totalStats: {
        totalHoursQuota,
        totalScheduledHours,
        totalActualHours,
        totalPlannedAmount,
        totalActualAmount,
        changeableBalance,
      },
    };
  }, [filteredData]);

  useEffect(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue(
      '--text-color-secondary',
    );
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    // Chart 1: Hours comparison (scheduled vs actual)
    const hoursChartData = {
      labels: courseSummaries.map((course) => course.courseName),
      datasets: [
        {
          type: 'bar',
          label: 'מכסת שעות',
          backgroundColor: Colors.expectedExpensesBackground,
          borderColor: Colors.expectedExpensesBorder,
          borderWidth: 1,
          data: courseSummaries.map((course) => course.totalHours),
        },
        {
          type: 'bar',
          label: 'שעות ששובצו',
          backgroundColor: Colors.actualExpensesBackground,
          borderColor: Colors.actualExpensesBorder,
          borderWidth: 1,
          data: courseSummaries.map((course) => course.scheduledHours),
        },
        {
          type: 'bar',
          label: 'שעות שבוצעו',
          backgroundColor: Colors.actualIncomesBackground,
          borderColor: Colors.actualIncomesBorder,
          borderWidth: 1,
          data: courseSummaries.map((course) => course.actualHours),
        },
      ],
    };

    // Chart 2: Amount comparison (planned vs actual)
    const amountChartData = {
      labels: courseSummaries.map((course) => course.courseName),
      datasets: [
        {
          type: 'bar',
          label: 'סכום מתוכנן',
          backgroundColor: Colors.expectedExpensesBackground,
          borderColor: Colors.expectedExpensesBorder,
          borderWidth: 1,
          data: courseSummaries.map((course) => course.plannedAmount),
        },
        {
          type: 'bar',
          label: 'סכום שבוצע',
          backgroundColor: Colors.actualExpensesBackground,
          borderColor: Colors.actualExpensesBorder,
          borderWidth: 1,
          data: courseSummaries.map((course) => course.actualAmount),
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      datasets: {
        bar: {
          categoryPercentage: 0.1, // Use full category width
          barPercentage: 2.5, // Use full bar width
        },
      },
      plugins: {
        tooltip: {
          mode: 'index' as const,
          intersect: false,
        },
        legend: {
          labels: {
            color: textColor,
            usePointStyle: true,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
      },
      barPercentage: 0.6,
      categoryPercentage: 0.8,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
    };

    setChartData1(hoursChartData);
    setChartData2(amountChartData);
    setChartOptions(options);
  }, [courseSummaries]);

  const getStatusSeverity = (course: CourseExpenseSummary) => {
    const execute = course.actualAmount;
    const planned = course.plannedAmount;

    if (execute - planned > 0) {
      return 'danger';
    } else {
      return 'success';
    }
  };

  const getStatusLabel = (course: CourseExpenseSummary) => {
    const execute = course.actualAmount;
    const planned = course.plannedAmount;

    if (execute - planned > 0) {
      return 'חריגה';
    } else {
      return 'תקין';
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Year Filter */}
      {yearOptions.length > 0 && (
        <YearFilterChips
          yearOptions={yearOptions}
          selectedYears={selectedYears}
          onToggleYear={toggleYear}
        />
      )}

      {/* Expense Summary Cards */}
      <div style={{ marginBottom: '32px' }}>
        <h2
          style={{
            textAlign: 'right',
            marginBottom: '24px',
            color: '#374151',
            fontSize: '1.5rem',
            fontWeight: 600,
          }}
        >
          הוצאות - סיכום כללי
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          <Asd
            str={`${Math.round(totalStats.totalHoursQuota)}`}
            title={'מכסת שעות'}
            icon={<Target color="#6366f1" size={32} />}
          />
          <Asd
            str={`${Math.round(totalStats.totalScheduledHours)}`}
            title={'שעות ששובצו'}
            icon={<Clock color="#8b5cf6" size={32} />}
          />
          <Asd
            str={`${Math.round(totalStats.totalActualHours)}`}
            title={'שעות שבוצעו'}
            icon={<CheckCircle color="#10b981" size={32} />}
          />
          <Asd
            str={`${Math.round(totalStats.totalPlannedAmount).toLocaleString()}₪`}
            title={'סכום מתוכנן'}
            icon={<Calculator color="#f59e0b" size={32} />}
          />
          <Asd
            str={`${Math.round(totalStats.totalActualAmount).toLocaleString()}₪`}
            title={'סכום שבוצע'}
            icon={<CheckCircle color="#059669" size={32} />}
          />
          <Asd
            str={`${Math.round(totalStats.changeableBalance).toLocaleString()}₪`}
            title={'יתרה ניתנת לשינוי'}
            icon={
              <AlertTriangle
                color={
                  totalStats.changeableBalance >= 0 ? '#22c55e' : '#ef4444'
                }
                size={32}
              />
            }
          />
        </div>
      </div>

      {/* Hours Chart */}
      <div style={{ marginBottom: '2rem' }}>
        <h3
          style={{ textAlign: 'right', marginBottom: '1rem', color: '#374151' }}
        >
          השוואת שעות - משובצות מול מבוצעות
        </h3>
        <Chart type="bar" data={chartData1} options={chartOptions} />
      </div>

      {/* Amount Chart */}
      <div style={{ marginBottom: '2rem' }}>
        <h3
          style={{ textAlign: 'right', marginBottom: '1rem', color: '#374151' }}
        >
          השוואת סכומים - מתוכנן מול מבוצע
        </h3>
        <Chart type="bar" data={chartData2} options={chartOptions} />
      </div>

      {/* Data Table */}
      <DataTable
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>}
        value={courseSummaries}
      >
        <Column
          header=""
          field={''}
          body={() => <Checkbox checked={true} />}
          style={{ width: '100px', textAlign: 'right' }}
        />
        <Column header="שם הקורס" field={'courseName'} style={style1} />
        <Column
          header="מכסת שעות"
          field={'totalHours'}
          body={(course: CourseExpenseSummary) => course.totalHours}
          style={style1}
        />
        <Column
          header="שעות ששובצו"
          field={'scheduledHours'}
          body={(course: CourseExpenseSummary) => course.scheduledHours}
          style={style1}
        />
        <Column
          header="שעות שבוצעו"
          field={'actualHours'}
          body={(course: CourseExpenseSummary) => course.actualHours}
          style={style1}
        />
        <Column
          header="סכום מתוכנן"
          field={'plannedAmount'}
          body={(course: CourseExpenseSummary) =>
            `${course.plannedAmount.toLocaleString()}₪`
          }
          style={style1}
        />
        <Column
          header="סכום שבוצע"
          field={'actualAmount'}
          body={(course: CourseExpenseSummary) =>
            `${course.actualAmount.toLocaleString()}₪`
          }
          style={style1}
        />
        <Column
          field={''}
          header={
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                alignItems: 'center',
              }}
              className="flex align-items-center gap-2"
            >
              סטטוס
              <Tooltip target=".status-info-icon" position="top" />
              <i
                className="pi pi-info-circle status-info-icon"
                data-pr-tooltip="סטטוס חריגת שעות מתוכננת אל מול מבוצעת"
                style={{
                  cursor: 'pointer',
                  color: '#6366F1',
                  fontSize: '1rem',
                }}
              />
            </div>
          }
          body={(course: CourseExpenseSummary) => (
            <Tag
              style={{ width: '97px' }}
              severity={getStatusSeverity(course)}
              value={getStatusLabel(course)}
            />
          )}
          style={style1}
        />
      </DataTable>
    </div>
  );
};
